import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  AlertTriangle, 
  Clock, 
  Zap, 
  X, 
  CreditCard,
  Calendar,
  Coins
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const UpgradePrompts = () => {
  const { user } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptType, setPromptType] = useState('');
  const [dismissed, setDismissed] = useState(new Set());
  
  const handleUpgrade = async (planType) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/payments/create-checkout`, {
        plan_type: planType
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to create payment session');
    }
  };

  useEffect(() => {
    if (!user || user.is_premium) return;

    const checkPromptConditions = () => {
      const trialExpiresAt = new Date(user.trial_expires_at);
      const now = new Date();
      const daysLeft = Math.ceil((trialExpiresAt - now) / (1000 * 60 * 60 * 24));
      const credits = user.credits || 0;

      // Low credits prompt
      if (credits < 10 && !dismissed.has('low-credits')) {
        setPromptType('low-credits');
        setShowPrompt(true);
        return;
      }

      // Trial ending soon (2 days left)
      if (daysLeft <= 2 && daysLeft > 0 && !dismissed.has('trial-ending')) {
        setPromptType('trial-ending');
        setShowPrompt(true);
        return;
      }

      // Trial expired
      if (daysLeft <= 0 && !dismissed.has('trial-expired')) {
        setPromptType('trial-expired');
        setShowPrompt(true);
        return;
      }

      // Success prompt after creating a book
      if (user.books_generated > 0 && credits > 20 && !dismissed.has('success-upsell')) {
        setPromptType('success-upsell');
        setShowPrompt(true);
        return;
      }
    };

    checkPromptConditions();
    
    // Check every hour
    const interval = setInterval(checkPromptConditions, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, dismissed]);

  const dismissPrompt = () => {
    setDismissed(prev => new Set([...prev, promptType]));
    setShowPrompt(false);
    
    // Store dismissal in localStorage to persist across sessions
    const dismissedPrompts = JSON.parse(localStorage.getItem('dismissedPrompts') || '[]');
    localStorage.setItem('dismissedPrompts', JSON.stringify([...dismissedPrompts, promptType]));
  };

  // Load dismissed prompts on mount
  useEffect(() => {
    const dismissedPrompts = JSON.parse(localStorage.getItem('dismissedPrompts') || '[]');
    setDismissed(new Set(dismissedPrompts));
  }, []);

  if (!showPrompt || !user || user.is_premium) return null;

  const renderPrompt = () => {
    switch (promptType) {
      case 'low-credits':
        return (
          <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div className="bg-red-500/20 rounded-full p-2">
                  <Coins className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Low Credits Warning</h3>
                  <p className="text-gray-300 text-sm mb-3">
                    You have <span className="font-semibold text-red-400">{user.credits} credits</span> remaining. 
                    You need 10 credits to create a complete book.
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleUpgrade('monthly')}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Upgrade Now
                    </button>
                    <Link
                      to="/credits/purchase"
                      className="border border-red-500 text-red-400 hover:bg-red-500 hover:text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Buy Credits
                    </Link>
                  </div>
                </div>
              </div>
              <button
                onClick={dismissPrompt}
                className="text-gray-400 hover:text-gray-300 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );

      case 'trial-ending':
        const daysLeft = Math.ceil((new Date(user.trial_expires_at) - new Date()) / (1000 * 60 * 60 * 24));
        return (
          <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div className="bg-orange-500/20 rounded-full p-2">
                  <Clock className="h-5 w-5 text-orange-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Trial Ending Soon!</h3>
                  <p className="text-gray-300 text-sm mb-3">
                    Your free trial expires in <span className="font-semibold text-orange-400">{daysLeft} day{daysLeft !== 1 ? 's' : ''}</span>. 
                    Don't lose access to your book creation tools!
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleUpgrade('monthly')}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Upgrade to Pro ($19/month)
                    </button>
                  </div>
                </div>
              </div>
              <button
                onClick={dismissPrompt}
                className="text-gray-400 hover:text-gray-300 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );

      case 'trial-expired':
        return (
          <div className="bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-500/50 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div className="bg-red-500/20 rounded-full p-2">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Trial Expired</h3>
                  <p className="text-gray-300 text-sm mb-3">
                    Your free trial has ended. Upgrade now to continue creating and selling books!
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleUpgrade('monthly')}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Upgrade Now
                    </button>
                    <Link
                      to="/credits/purchase"
                      className="border border-red-500 text-red-400 hover:bg-red-500 hover:text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Buy Credits Instead
                    </Link>
                  </div>
                </div>
              </div>
              <button
                onClick={dismissPrompt}
                className="text-gray-400 hover:text-gray-300 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );

      case 'success-upsell':
        return (
          <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/30 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div className="bg-green-500/20 rounded-full p-2">
                  <Zap className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Great Progress! Ready for More?</h3>
                  <p className="text-gray-300 text-sm mb-3">
                    You've created <span className="font-semibold text-green-400">{user.books_generated} book{user.books_generated !== 1 ? 's' : ''}</span>! 
                    Upgrade to Pro and get 75 credits monthly to scale your publishing business.
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleUpgrade('monthly')}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Scale with Pro ($19/month)
                    </button>
                  </div>
                </div>
              </div>
              <button
                onClick={dismissPrompt}
                className="text-gray-400 hover:text-gray-300 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed top-24 right-6 max-w-sm z-50 animate-slide-in-right">
      {renderPrompt()}
    </div>
  );
};

// Top banner component for critical messages
export const TrialBanner = () => {
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  
  const handleUpgrade = async (planType) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/payments/create-checkout`, {
        plan_type: planType
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to create payment session');
    }
  };

  useEffect(() => {
    if (!user || user.is_premium) {
      setShowBanner(false);
      return;
    }

    const trialExpiresAt = new Date(user.trial_expires_at);
    const now = new Date();
    const daysLeft = Math.ceil((trialExpiresAt - now) / (1000 * 60 * 60 * 24));

    // Show banner when 2 days left or expired
    setShowBanner(daysLeft <= 2);
  }, [user]);

  if (!showBanner || !user || user.is_premium) return null;

  const trialExpiresAt = new Date(user.trial_expires_at);
  const now = new Date();
  const daysLeft = Math.ceil((trialExpiresAt - now) / (1000 * 60 * 60 * 24));
  const isExpired = daysLeft <= 0;

  return (
    <div className={`w-full ${isExpired ? 'bg-red-600' : 'bg-orange-600'} text-white py-3 px-4 text-center relative z-40`}>
      <div className="flex items-center justify-center space-x-4">
        <div className="flex items-center space-x-2">
          {isExpired ? (
            <AlertTriangle className="h-4 w-4" />
          ) : (
            <Clock className="h-4 w-4" />
          )}
          <span className="font-semibold text-sm">
            {isExpired 
              ? "Trial Expired - Upgrade to continue creating books"
              : `Trial expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} - Upgrade now!`
            }
          </span>
        </div>
        <button
          onClick={() => handleUpgrade('monthly')}
          className="bg-white text-gray-900 px-4 py-1 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors"
        >
          Upgrade Now
        </button>
      </div>
    </div>
  );
};

export default UpgradePrompts;